import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import UsageAnalytics from './UsageAnalytics';
import { 
  CreditCard, 
  Calendar, 
  Clock, 
  DollarSign, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Users,
  Mic,
  MessageSquare,
  HardDrive,
  Loader2,
  ExternalLink,
  RefreshCw,
  Wallet,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const BillingSettings = () => {
  const [subscription, setSubscription] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch subscription
      const subResponse = await fetch(`${BACKEND_URL}/api/user/subscription`, {
        credentials: 'include'
      });
      
      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData);
      }

      // Fetch entitlements
      const entResponse = await fetch(`${BACKEND_URL}/api/user/entitlements`, {
        credentials: 'include'
      });

      if (entResponse.ok) {
        const entData = await entResponse.json();
        setEntitlements(entData.entitlements || []);
      }

      // Fetch wallet balance (if wallet system is enabled)
      try {
        const walletResponse = await fetch(`${BACKEND_URL}/api/wallet/balance`, {
          credentials: 'include'
        });

        if (walletResponse.ok) {
          const walletData = await walletResponse.json();
          setWalletBalance(walletData);
        }
      } catch (walletErr) {
        // Wallet system might not be enabled, silently ignore
        console.log('Wallet system not available:', walletErr);
      }

    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    // Navigate to homepage pricing section with smooth scroll
    window.location.href = '/#pricing';
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create portal session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Customer Portal
      window.location.href = data.portal_url;
    } catch (err) {
      console.error('Error opening customer portal:', err);
      if (err.message.includes('No active subscription')) {
        alert('Please upgrade to a paying plan to access billing management.');
      } else {
        alert(`Error: ${err.message}`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to permanently delete your account? This action cannot be undone and will remove all your data from SaaS-Base.'
    );
    
    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/delete-account`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete account');
      }

      // Logout and redirect to homepage
      window.location.href = '/';
    } catch (err) {
      console.error('Error deleting account:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'trialing': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'past_due': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      case 'canceled': return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'trialing': return <Clock className="w-4 h-4" />;
      case 'past_due': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getFeatureIcon = (featureKey) => {
    switch (featureKey) {
      case 'voice_minutes': return <Mic className="w-4 h-4" />;
      case 'sms_count': return <MessageSquare className="w-4 h-4" />;
      case 'storage_gb': return <HardDrive className="w-4 h-4" />;
      case 'contacts_limit': return <Users className="w-4 h-4" />;
      case 'team_members': return <Users className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getFeatureName = (featureKey) => {
    const names = {
      'voice_minutes': 'Voice Minutes',
      'sms_count': 'SMS Messages', 
      'storage_gb': 'Storage (GB)',
      'contacts_limit': 'Contacts',
      'team_members': 'Team Members',
      'ai_insights': 'AI Insights',
      'gift_planning': 'Gift Planning',
      'social_radar': 'Social Radar',
      'advanced_analytics': 'Advanced Analytics',
      'priority_support': 'Priority Support',
      'white_label': 'White Label'
    };
    return names[featureKey] || featureKey;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Billing & Usage</h2>
          <p className="text-gray-600 dark:text-gray-300">Manage your subscription and track usage</p>
        </div>
        <Button onClick={fetchBillingData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Billing tabs">
            {[
              { id: 'overview', label: 'Overview', icon: CreditCard },
              { id: 'usage', label: 'Usage Analytics', icon: BarChart3 },
              { id: 'history', label: 'Billing History', icon: Calendar }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Wallet Balance */}
              {walletBalance && (
                <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <Wallet className="w-5 h-5" />
                      Wallet Balance
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${
                      walletBalance.status === 'active' ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' :
                      walletBalance.status === 'low_balance' ? 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20' :
                      'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20'
                    }`}>
                      {walletBalance.status === 'active' ? <CheckCircle className="w-4 h-4" /> : 
                       walletBalance.status === 'low_balance' ? <AlertCircle className="w-4 h-4" /> :
                       <AlertCircle className="w-4 h-4" />}
                      {walletBalance.status?.charAt(0).toUpperCase() + walletBalance.status?.slice(1).replace('_', ' ')}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        ${walletBalance.balance_usd.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        Available for usage billing
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Low balance alert at ${(walletBalance.low_balance_threshold_cents / 100).toFixed(2)}
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Button 
                        onClick={() => window.location.href = '/app/settings?tab=wallet'}
                        variant="outline" 
                        size="sm"
                        className="w-full"
                      >
                        <Wallet className="w-4 h-4 mr-2" />
                        Manage Wallet
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Subscription */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Current Subscription</h3>
                  {subscription?.subscription && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(subscription.subscription.status)}`}>
                      {getStatusIcon(subscription.subscription.status)}
                      {subscription.subscription.status?.charAt(0).toUpperCase() + subscription.subscription.status?.slice(1)}
                    </span>
                  )}
                </div>

                {subscription?.subscription ? (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 dark:text-gray-100">Plan Details</h4>
                        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                          <div className="flex justify-between">
                            <span>Plan:</span>
                            <span className="font-medium">{subscription.plan?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Status:</span>
                            <span className="font-medium">{subscription.subscription.status}</span>
                          </div>
                          {subscription.subscription.trial_end && (
                            <div className="flex justify-between">
                              <span>Trial Ends:</span>
                              <span className="font-medium">{formatDate(subscription.subscription.trial_end)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span>Next Billing:</span>
                            <span className="font-medium">{formatDate(subscription.subscription.current_period_end)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 dark:text-gray-100">Actions</h4>
                        <div className="space-y-2">
                          <Button 
                            onClick={handleManageSubscription}
                            variant="outline" 
                            size="sm"
                            className="w-full"
                            disabled={actionLoading}
                          >
                            {actionLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-2" />
                            )}
                            Manage Subscription
                          </Button>
                          <Button 
                            onClick={handleUpgrade}
                            variant="outline" 
                            size="sm"
                            className="w-full"
                          >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Change Plan
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No Active Subscription</h4>
                    <p className="text-gray-600 mb-4 dark:text-gray-300">
                      You're currently on the free plan. Upgrade to unlock all features.
                    </p>
                    <div className="space-y-3">
                      <Button onClick={handleUpgrade} className="w-full">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Upgrade to a Paying Plan
                      </Button>
                      <Button 
                        onClick={handleCancelAccount}
                        variant="outline" 
                        className="w-full text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/10"
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mr-2" />
                        )}
                        Cancel Account Completely
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Usage & Entitlements */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">Usage & Limits</h3>

                {entitlements.length > 0 ? (
                  <div className="space-y-4">
                    {/* Usage-based entitlements */}
                    {entitlements.filter(e => e.allocation_limit !== null).map((entitlement) => {
                      const percentage = getUsagePercentage(entitlement.usage_count, entitlement.allocation_limit);
                      const isUnlimited = entitlement.allocation_limit === -1;
                      
                      return (
                        <div key={entitlement.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getFeatureIcon(entitlement.feature_key)}
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {getFeatureName(entitlement.feature_key)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              {isUnlimited ? (
                                <span className="text-green-600 font-medium">Unlimited</span>
                              ) : (
                                <span>
                                  {entitlement.usage_count} / {entitlement.allocation_limit}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {!isUnlimited && (
                            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(percentage)}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Feature entitlements (boolean) */}
                    {entitlements.filter(e => e.allocation_limit === null).length > 0 && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="font-medium text-gray-900 mb-3 dark:text-gray-100">Enabled Features</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {entitlements.filter(e => e.allocation_limit === null).map((entitlement) => (
                            <div key={entitlement.id} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {getFeatureName(entitlement.feature_key)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No Entitlements Found</h4>
                    <p className="text-gray-600 mb-4 dark:text-gray-300">
                      Subscribe to a plan to access premium features and allocations.
                    </p>
                    <Button onClick={handleUpgrade} variant="outline">
                      View Plans
                    </Button>
                  </div>
                )}
              </div>

              {/* Billing History */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">Billing History</h3>
                
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No Billing History</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Your billing history and invoices will appear here once you have an active subscription.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'usage' && <UsageAnalytics />}

          {activeTab === 'history' && (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Billing History</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Your billing history and invoices will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;